import React, { useState } from 'react';
import Icon from './icons';
import { useTheme } from '../hooks/useTheme';
import { useAuth } from '../context/AuthContext';

export default function Sidebar({ page, setPage, onLogout, collapsed, onToggleCollapse }) {
  const { theme, toggleTheme, isDark } = useTheme();
  const { user, profile } = useAuth();

  const links = [
    { id: 'home', label: 'Home', icon: Icon.home },
    { id: 'extract', label: 'Product Queue', icon: Icon.extract },
    { id: 'catalog', label: 'My Products', icon: Icon.catalog },
    { id: 'analytics', label: 'Analytics', icon: Icon.analytics },
    { id: 'orders', label: 'Orders', icon: Icon.orders },
    { id: 'monitor', label: 'Monitor', icon: Icon.monitor },
    { id: 'research', label: 'Research', icon: Icon.research },
    { id: 'ai-tools', label: 'AI Tools', icon: Icon.sparkle },
    { id: 'settings', label: 'Settings', icon: Icon.settings },
  ];

  const w = collapsed ? 60 : 220;

  return (
    <aside style={{ width: w, minHeight: '100vh', background: 'var(--color-bg-base)', borderRight: '1px solid var(--color-border)', padding: `${collapsed ? '1rem' : '1.5rem'} 0`, display: 'flex', flexDirection: 'column', position: 'fixed', left: 0, top: 0, bottom: 0, zIndex: 50, transition: 'width 0.2s ease' }}>
      {/* Logo */}
      <div style={{ padding: collapsed ? '0 0.5rem' : '0 1.25rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: collapsed ? 0 : '0.75rem', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <div style={{ width: '36px', height: '36px', background: 'linear-gradient(135deg, #FF6B35, #FF8A5C)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1rem', fontWeight: 700, color: '#fff', flexShrink: 0, boxShadow: '0 4px 12px rgba(255,107,53,0.3)' }}>A</div>
        {!collapsed && (
          <div>
            <div style={{ fontWeight: 700, fontSize: '0.9rem', letterSpacing: '-0.01em', color: 'var(--color-text-primary)' }}>AMORLIA</div>
            <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.7rem' }}>JEWELS</div>
          </div>
        )}
      </div>

      {/* Toggle */}
      <button onClick={onToggleCollapse} style={{ background: 'none', border: 'none', color: 'var(--color-text-muted)', cursor: 'pointer', padding: '0.25rem', margin: collapsed ? '0 auto' : '0 0.75rem', marginBottom: '0.75rem', display: 'flex', borderRadius: '0.25rem' }}>
        {collapsed ? Icon.chevronRight(16) : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>}
      </button>

      {/* Nav Links */}
      <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '2px', padding: '0 0.5rem', overflowY: 'auto' }}>
        {links.map(l => (
          <button
            key={l.id}
            onClick={() => setPage(l.id)}
            title={collapsed ? l.label : undefined}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem',
              padding: collapsed ? '0.625rem' : '0.625rem 0.875rem',
              background: page === l.id ? 'var(--color-bg-card-hover)' : 'transparent',
              color: page === l.id ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
              fontSize: '0.875rem', fontWeight: page === l.id ? 600 : 400,
              textAlign: 'left', width: '100%', transition: 'all 0.15s',
              justifyContent: collapsed ? 'center' : 'flex-start',
            }}
          >
            {l.icon(18)}
            {!collapsed && l.label}
          </button>
        ))}
      </nav>

      {/* Bottom section */}
      <div style={{ padding: '0 0.5rem', display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {/* Theme toggle */}
        <button
          onClick={toggleTheme}
          title={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: collapsed ? '0.625rem' : '0.625rem 0.875rem',
            background: 'transparent', color: 'var(--color-text-muted)',
            border: '1px solid var(--color-border)', borderRadius: '0.375rem',
            cursor: 'pointer', fontSize: '0.8rem', width: '100%',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          {isDark ? Icon.sun(16) : Icon.moon(16)}
          {!collapsed && (isDark ? 'Light Mode' : 'Dark Mode')}
        </button>

        {/* User info */}
        {user && !collapsed && (
          <div style={{ padding: '0.5rem 0.75rem', background: 'var(--color-bg-card)', borderRadius: '0.375rem', marginBottom: '0.25rem' }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-primary)', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {profile?.display_name || user.email?.split('@')[0]}
            </div>
            <div style={{ fontSize: '0.65rem', color: 'var(--color-text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user.email}
            </div>
          </div>
        )}

        {/* Sign out */}
        <button
          onClick={onLogout}
          style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem',
            padding: collapsed ? '0.625rem' : '0.625rem 0.875rem',
            background: 'transparent', color: 'var(--color-text-muted)',
            border: '1px solid var(--color-border)', borderRadius: '0.375rem',
            cursor: 'pointer', fontSize: '0.8rem', width: '100%',
            justifyContent: collapsed ? 'center' : 'flex-start',
          }}
        >
          {Icon.logout(16)} {!collapsed && 'Sign Out'}
        </button>
      </div>
    </aside>
  );
}
